import type { NavigateFunction } from 'react-router-dom';
import type { AppToolLink, AppToolLinkInput } from '@shared/app-tool-links';
import type { UsageSheetLinkField } from '@shared/types/forge-usage-sheet';
import { supabase } from './supabase';
import { resolveToolRuntime } from './prebuilt-tools';

type LinkRow = {
  id: string;
  source_app_id: string;
  fields: unknown;
  statuses: string[] | null;
  target: { id: string; title: string; inputs: unknown; app: { key: string } | null } | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toInputs = (value: unknown): AppToolLinkInput[] => (Array.isArray(value) ? value : []).flatMap((input) =>
  isRecord(input) && typeof input.key === 'string'
    ? [{
      key: input.key,
      label: typeof input.label === 'string' ? input.label : input.key,
      required: input.required === true,
      ...(typeof input.defaultValue === 'string' && input.defaultValue ? { defaultValue: input.defaultValue } : {}),
    }]
    : []);

const toFields = (value: unknown): UsageSheetLinkField[] => (Array.isArray(value) ? value : []).flatMap((field) =>
  isRecord(field) && typeof field.input === 'string' && typeof field.column === 'string'
    ? [{
      input: field.input,
      inputLabel: typeof field.inputLabel === 'string' ? field.inputLabel : field.input,
      column: field.column,
      columnLabel: typeof field.columnLabel === 'string' ? field.columnLabel : field.column,
    }]
    : []);

/** S1: the tools proposed on the objects of an app, read under the person's rights. A tool they cannot open is left out. */
export const loadAppToolLinks = async (appId: string): Promise<AppToolLink[]> => {
  const { data, error } = await supabase
    .from('app_tool_links')
    .select('id, source_app_id, fields, statuses, target:flows!app_tool_links_target_flow_id_fkey(id, title, inputs, app:tool_apps!flows_app_id_fkey(key))')
    .eq('source_app_id', appId)
    .order('created_at');
  if (error) throw new Error('Les outils proposés sur cette liste n’ont pas pu être chargés.');
  return ((data ?? []) as unknown as LinkRow[]).flatMap((row) => row.target?.app?.key
    ? [{
      id: row.id,
      sourceAppId: row.source_app_id,
      target: { flowId: row.target.id, title: row.target.title, appKey: row.target.app.key, inputs: toInputs(row.target.inputs) },
      fields: toFields(row.fields),
      statuses: row.statuses ?? [],
    }]
    : []);
};

/** Opens the tool's form, prefilled from the object: the person launches it. */
export const openLinkedTool = (navigate: NavigateFunction, link: AppToolLink, prefill: Record<string, string>): void => {
  navigate(`/apps/${encodeURIComponent(link.target.appKey)}`, { state: { toolPrefill: prefill, toolLaunch: true } });
};

/** One launch of the tool for one object, with values the object fully gives (a selection). */
export const launchLinkedTool = async (link: AppToolLink, values: Record<string, string>): Promise<void> => {
  const runtime = await resolveToolRuntime({
    flowId: link.target.flowId,
    submission: { kind: 'provide_inputs', inputValues: values },
    clientContext: { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, locale: navigator.language },
    handoffOrigin: null,
    proposalHandoff: null,
    launchContext: null,
  });
  if (runtime.status !== 'running' && runtime.status !== 'blocked') {
    throw new Error(`« ${link.target.title} » n’a pas pu être lancé.`);
  }
};
